import {Component, DoCheck, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild, AfterViewInit} from "@angular/core";
import {LiveAnnouncer} from "@angular/cdk/a11y";

export class ValidationResult {
  message?: string;

  constructor(msg: string) {
    this.message = msg;
  }
}

@Component({
  standalone: false,
  selector: "wfm-textbox",
  templateUrl: "./wfm-textbox.component.html",
  styleUrls: ["./wfm-textbox.component.scss"]
})
export class WfmTextboxComponent implements OnInit, AfterViewInit, DoCheck {
  @Input() required = false;
  @Input() label = null;
  @Input() textValue = "";
  @Input() placeHolder = "";
  @Input() validators: Array<(value: any) => ValidationResult>;
  @Input() serverSideErrMsgs: Array<string>;
  @Input() clientSideErrMsgs: Array<string>;
  @Input() showMultilineError: Boolean;
  @Input() onEventHideServerSideError = true;
  @Input() inlineStyleObject = {};
  @Input() wfmAutoComplete = "on";
  @Input() inputId = null;
  @Input() inputName = null;
  @Input() inputType = "text";
  @Input() maxValue = null;
  @Input() minValue = null;
  @Input() maxLength: Number = null;

  @Input() hasFocus = false;

  untouchedInput = true;

  get untouched() {
    return this.untouchedInput;
  }

  get isValid() {
    if (this.untouched) {
      return true;
    }
    return (
      this.serverSideErrMsgs.length < 1 && this.clientSideErrMsgs.length < 1
    );
  }

  @Output() wfmTextboxEvent: EventEmitter<any> = new EventEmitter<any>();
  @Output() inputOnBlur: EventEmitter<any> = new EventEmitter<any>();
  @Output() inputOnInput: EventEmitter<any> = new EventEmitter<any>();

  @ViewChild("input") inputEl: ElementRef;

  // Last error string pushed to the screen-reader live announcer. Guards
  // ngDoCheck so the same error is not re-announced on every change-detection
  // pass (WCAG 4.1.3).
  private lastAnnouncedError = "";

  constructor(private liveAnnouncer: LiveAnnouncer) {
    this.validators = [];
    this.serverSideErrMsgs = [];
    this.clientSideErrMsgs = [];
    this.showMultilineError = false;
  }

  ngOnInit() {
    
  }

  ngAfterViewInit():void {
    if(this.hasFocus){
      this.inputEl.nativeElement.focus();
    }
  }

  // WCAG 4.1.3 Status Messages: announce validation errors the moment they
  // appear without keeping a persistent, browsable error text node in the DOM.
  //
  // Previously the error text lived in an always-present sr-only
  // role="alert" span. That span WAS in the accessibility tree, so JAWS/NVDA
  // browse-mode (arrow) navigation stopped on it as a separate line — an extra
  // focus stop on "This field is required" ON TOP of the error already read via
  // aria-describedby on the input. Using the CDK LiveAnnouncer instead announces
  // the error through a shared, transient off-screen live region (cleared by
  // CDK), so there is no standalone error node for the virtual cursor to land
  // on, while the input still exposes the error on focus via aria-describedby
  // pointing at the (aria-hidden) visible message.
  ngDoCheck(): void {
    const message = this.hasErrorMsg() ? this.getAnnouncedError() : "";
    if (message !== this.lastAnnouncedError) {
      this.lastAnnouncedError = message;
      if (message) {
        this.liveAnnouncer.announce(message, "assertive");
      }
    }
  }

  // Label-prefixed error string ("NAME: This field is required") used for the
  // spoken announcement so it is unambiguous when several fields can error; when
  // no label is set the raw message is announced on its own.
  private getAnnouncedError(): string {
    const msg = this.getSingleError();
    if (!msg) {
      return "";
    }
    return this.label ? `${this.label}: ${msg}` : msg;
  }

  validate() {
    // if (this.serverSideErrMsgs.length > 0) {
    //   this.serverSideErrMsgs = [];
    // }
    if (this.clientSideErrMsgs.length > 0) {
      this.clientSideErrMsgs = [];
    }
    this.validators.forEach(validator => {
      const result: ValidationResult = validator.call(
        this,
        this.inputEl.nativeElement.value
      );
      if (result.message !== null && result.message !== "") {
        this.clientSideErrMsgs.push(result.message);
      }
    });
    return this.isValid;
  }

  onBlur(event) {
    if (this.validators.length > 0) {
      this.updateValue(event);
    }
    this.inputOnBlur.emit(event);
    this.hasFocus = false;
  }

  onFocus(event) {
    this.hasFocus = true;
  }

  updateValue(event) {
    this.untouchedInput = false;
    this.validate();
    this.textValue = event.target.value;
    this.wfmTextboxEvent.emit(event);
    this.inputOnInput.emit(event);
  }

  hasErrorMsg() {
    if (this.serverSideErrMsgs.length > 0) {
      return true;
    } else if (!this.untouchedInput && this.clientSideErrMsgs.length > 0) {
      return true;
    }
    return false;
  }

  getSingleError() {
    if (this.serverSideErrMsgs.length > 0) {
      return this.serverSideErrMsgs[0];
    } else if (this.clientSideErrMsgs.length > 0) {
      return this.clientSideErrMsgs[0];
    }
    return null;
  }

  get hasServerError() {
    return this.serverSideErrMsgs.length > 0;
  }

  get hasClientError() {
    return this.clientSideErrMsgs.length > 0;
  }

  setFocus() {
    this.inputEl.nativeElement.focus();
  }

  setValue(value: string) {
    this.untouchedInput = false;
    this.textValue = value;
    this.validate();
  }
}
